import { useState } from "react"
import { Check, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { DatePicker } from "@/components/ui/DatePicker"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover"
import { Separator } from "@/components/ui/Separator"
import { formatRange } from "../format"

export interface PeriodValue<K extends string> {
  preset: K | "custom"
  from: string
  to: string
}

export interface PeriodPreset<K extends string> {
  key: K
  label: string
  /** Draw a divider above this preset, to group presets by length. */
  dividerBefore?: boolean
}

interface Props<K extends string> {
  value: PeriodValue<K>
  presets: PeriodPreset<K>[]
  onChange: (patch: { preset: K | "custom"; from?: string; to?: string }) => void
  /** Bounds for the custom dates (ISO). */
  minDate?: string
  maxDate?: string
}

function Row({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 w-full justify-between px-2 text-xs"
      style={{ color: active ? "var(--primary)" : "var(--foreground)", fontWeight: active ? 600 : 400 }}
      onClick={onClick}
    >
      {label}
      {active && <Check className="h-3.5 w-3.5 text-primary" />}
    </Button>
  )
}

/** Period picker: a button showing the current choice (the preset name, or
 * the dates for a custom range), a dropdown with the presets and a custom
 * from–to range. Presets apply and close; custom dates apply as picked and
 * keep the dropdown open. */
export default function PeriodMenu<K extends string>({ value, presets, onChange, minDate, maxDate }: Props<K>) {
  const [open, setOpen] = useState(false)
  const pick = (preset: K) => {
    onChange({ preset })
    setOpen(false)
  }
  const label = value.preset === "custom" ? formatRange(value.from, value.to) : presets.find((p) => p.key === value.preset)?.label

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-auto gap-2 px-3 py-1.5 text-left focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <span className="text-xs font-semibold text-foreground">{label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-1.5">
        <div className="px-2 py-1.5 text-xs font-semibold">Period</div>
        <Separator className="mb-1" />
        {presets.map((p) => (
          <div key={p.key}>
            {p.dividerBefore && <Separator className="my-1" />}
            <Row label={p.label} active={value.preset === p.key} onClick={() => pick(p.key)} />
          </div>
        ))}
        <Separator className="my-1" />
        <Row label="Custom" active={value.preset === "custom"} onClick={() => onChange({ preset: "custom" })} />
        <div className="flex items-center gap-1.5 px-2 pb-1 pt-1">
          <DatePicker value={value.from} onChange={(from) => onChange({ preset: "custom", from })} className="h-8 flex-1" fromDate={minDate} toDate={maxDate} />
          <span className="text-xs text-muted-foreground">to</span>
          <DatePicker value={value.to} onChange={(to) => onChange({ preset: "custom", to })} className="h-8 flex-1" fromDate={minDate} toDate={maxDate} />
        </div>
      </PopoverContent>
    </Popover>
  )
}
